import { h } from "snabbdom";
import type { VNode } from "snabbdom";
import type { Block, CardGroup, CardSummary, Document, Inline, Msg, ViewConfig } from "../../types";
import { previewHost } from "../../editor/preview";
import { PREVIEW_LINES } from "../../preview-limits";
import * as d3 from "d3";

// Node box size (px). Drives both the cardPreview render and the collision
// radius used for layout spacing.
const NODE_W = 200;
const NODE_H = 130;
// Group hub node size (px).
const HUB_W = 120;
const HUB_H = 44;

type Laid = { id: string; content: Document; x: number; y: number; group: string | null };
type Hub = { id: string; label: string; x: number; y: number };
type Edge = { sx: number; sy: number; tx: number; ty: number; group: string | null };
type ZoomCtrl = { zoom: d3.ZoomBehavior<HTMLElement, unknown>; fit: d3.ZoomTransform };

// Keyed by the .view-network container element. Lets the +/−/reset buttons find
// the live zoom behavior after a view-block re-render (when the insert hook,
// and thus any per-render closure, no longer fires).
const zoomRegistry = new WeakMap<HTMLElement, ZoomCtrl>();

type SimNode = d3.SimulationNodeDatum & { id: string; group: string | null; isHub: boolean };
type SimLink = d3.SimulationLinkDatum<SimNode> & { source: string; target: string; hub: boolean };

export function viewNetworkView(
  _config: ViewConfig,
  groups: CardGroup[],
  dispatch: (msg: Msg) => void,
  depth: number
): VNode {
  // Filtering + grouping are server-side. Flatten to cards plus a per-card group
  // label (the bucket the backend placed each card in).
  const cards: CardSummary[] = groups.flatMap((g) => g.cards);
  const groupOf = new Map<string, string | null>();
  for (const g of groups) {
    for (const card of g.cards) groupOf.set(card.id, g.group);
  }
  const { nodes, hubs, edges, width, height } = layout(cards, groupOf);

  // Buttons resolve the live zoom behavior from the registry via the clicked
  // element's container, so they keep working across re-renders.
  const control = (action: "in" | "out" | "reset") => (e: Event) => {
    const root = (e.currentTarget as HTMLElement).closest(".view-network") as HTMLElement | null;
    const ctrl = root ? zoomRegistry.get(root) : undefined;
    if (!root || !ctrl) return;
    const sel = d3.select(root).transition().duration(action === "reset" ? 200 : 150);
    if (action === "reset") sel.call(ctrl.zoom.transform, ctrl.fit);
    else sel.call(ctrl.zoom.scaleBy, action === "in" ? 1.2 : 1 / 1.2);
  };

  const nodeVnodes = nodes.map((n) => {
    const color = n.group != null ? groupColor(n.group) : null;
    return h(
      "div.network-node",
      {
        key: n.id,
        style: {
          left: `${n.x - NODE_W / 2}px`,
          top: `${n.y - NODE_H / 2}px`,
          width: `${NODE_W}px`,
          height: `${NODE_H}px`,
          ...(color ? { "border-color": color.border, "border-width": "2px" } : {}),
        },
        on: { click: () => dispatch({ type: "NavigateTo", id: n.id }) },
      },
      [
        ...(n.group != null && color
          ? [h("div.network-node__group", { style: { background: color.bg, color: color.text } }, n.group)]
          : []),
        previewHost(n.content, depth, dispatch, PREVIEW_LINES.network),
      ]
    );
  });

  const hubVnodes = hubs.map((hub) => {
    const color = groupColor(hub.label);
    return h(
      "div.network-hub",
      {
        key: hub.id,
        style: {
          left: `${hub.x - HUB_W / 2}px`,
          top: `${hub.y - HUB_H / 2}px`,
          width: `${HUB_W}px`,
          height: `${HUB_H}px`,
          background: color.bg,
          color: color.text,
          "border-color": color.border,
        },
      },
      hub.label
    );
  });

  return h("div.view-network", [
    h("div.network-controls", [
      h("button.network-btn", { attrs: { type: "button", title: "Zoom in" }, on: { click: control("in") } }, "+"),
      h("button.network-btn", { attrs: { type: "button", title: "Zoom out" }, on: { click: control("out") } }, "−"),
      h("button.network-btn", { attrs: { type: "button", title: "Reset view" }, on: { click: control("reset") } }, "⊡"),
    ]),
    h(
      "div.network-canvas",
      {
        style: { width: `${width}px`, height: `${height}px` },
        hook: {
          insert: (vnode) => {
            const canvas = vnode.elm as HTMLElement;
            const container = canvas.parentElement as HTMLElement;
            setupZoom(container, canvas, width, height);
          },
        },
      },
      [edgeLayer(edges, width, height), ...hubVnodes, ...nodeVnodes]
    ),
  ]);
}

function edgeLayer(edges: Edge[], width: number, height: number): VNode {
  return h(
    "svg.network-edges",
    { attrs: { width, height, viewBox: `0 0 ${width} ${height}` } },
    edges.map((e) => {
      // Hub (group-membership) edges are tinted by group and dashed to set them
      // apart from real card-to-card links.
      const hub = e.group != null;
      const color = hub ? groupColor(e.group!).border : null;
      return h("line", {
        class: { "network-edges__hub": hub },
        attrs: {
          x1: e.sx,
          y1: e.sy,
          x2: e.tx,
          y2: e.ty,
          ...(color ? { stroke: color } : {}),
        },
      });
    })
  );
}

// ── Pan/zoom ─────────────────────────────────────────────────────────────────

function setupZoom(container: HTMLElement, canvas: HTMLElement, width: number, height: number): void {
  const zoom = d3
    .zoom<HTMLElement, unknown>()
    .scaleExtent([0.2, 2.5])
    .on("zoom", (event) => {
      const t = event.transform;
      canvas.style.transform = `translate(${t.x}px, ${t.y}px) scale(${t.k})`;
    });

  d3.select(container).call(zoom).on("dblclick.zoom", null);

  // Initial transform: scale to fit the layout into the container, centered.
  const cw = container.clientWidth || 800;
  const ch = container.clientHeight || 480;
  const pad = 40;
  const k = Math.min((cw - pad) / width, (ch - pad) / height, 1);
  const tx = (cw - width * k) / 2;
  const ty = (ch - height * k) / 2;
  const fit = d3.zoomIdentity.translate(tx, ty).scale(k);

  zoomRegistry.set(container, { zoom, fit });
  d3.select(container).call(zoom.transform, fit);
}

// ── Layout ───────────────────────────────────────────────────────────────────

function layout(
  cards: CardSummary[],
  groupOf: Map<string, string | null>
): { nodes: Laid[]; hubs: Hub[]; edges: Edge[]; width: number; height: number } {
  const idSet = new Set(cards.map((c) => c.id));

  const cardNodes: SimNode[] = cards.map((c) => ({ id: c.id, group: groupOf.get(c.id) ?? null, isHub: false }));
  const simLinks: SimLink[] = [];
  for (const card of cards) {
    for (const target of collectLinks(card.content)) {
      if (target !== card.id && idSet.has(target)) {
        simLinks.push({ source: card.id, target, hub: false });
      }
    }
  }

  const collide = Math.hypot(NODE_W, NODE_H) / 2;

  const hubCollide = Math.hypot(HUB_W, HUB_H) / 2;

  // Each distinct group value gets a hub node; every member links to it. The
  // link force pulls a group's cards together, and hubs repel each other
  // (charge) so clusters separate — no synthetic position anchors needed.
  const groupVals = [...new Set(cardNodes.map((n) => n.group).filter((g): g is string => g != null))].sort();
  const hubId = (g: string): string => ` group:${g}`;
  const hubNodes: SimNode[] = groupVals.map((g, i) => {
    // Seed hubs on a ring so clusters start apart and settle quickly.
    const a = (i / Math.max(groupVals.length, 1)) * 2 * Math.PI;
    const ringR = collide * 2.5 * Math.max(groupVals.length, 1);
    return { id: hubId(g), group: g, isHub: true, x: Math.cos(a) * ringR, y: Math.sin(a) * ringR };
  });
  for (const n of cardNodes) {
    if (n.group != null) simLinks.push({ source: n.id, target: hubId(n.group), hub: true });
  }

  const simNodes: SimNode[] = [...cardNodes, ...hubNodes];

  const sim = d3
    .forceSimulation<SimNode>(simNodes)
    .force(
      "link",
      d3
        .forceLink<SimNode, SimLink>(simLinks)
        .id((d) => d.id)
        // Hub links sit a bit longer and pull harder, holding clusters together.
        .distance((l) => ((l as SimLink).hub ? collide * 3 : collide * 2.2))
        .strength((l) => ((l as SimLink).hub ? 0.5 : 0.3))
    )
    // Cap the repulsion range so distant (e.g. disconnected) nodes stop pushing
    // each other apart indefinitely.
    .force("charge", d3.forceManyBody().strength(-collide * 8).distanceMax(collide * 5))
    // Hub-only long-range repulsion: group nodes shove everything not bound to
    // them (other hubs, foreign cards) far away, driving clusters apart. Cards
    // emit nothing here (strength 0); their link to the hub still holds members
    // close, so only non-members are pushed off.
    .force(
      "hubCharge",
      d3
        .forceManyBody<SimNode>()
        .strength((n) => (n.isHub ? -collide * 200 : 0))
        .distanceMax(collide * 50)
    )
    .force("collide", d3.forceCollide<SimNode>((n) => (n.isHub ? hubCollide : collide * 1.05)))
    // Faint origin gravity keeps the graph from drifting off, but stays weak
    // enough that hub repulsion (inverse-square) can still shove non-members far.
    .force("x", d3.forceX(0).strength(0.01))
    .force("y", d3.forceY(0).strength(0.01))
    .stop();

  // Run to convergence synchronously — no animation, positions are final.
  const ticks = Math.min(400, Math.max(120, Math.ceil(120 * Math.sqrt(simNodes.length || 1))));
  sim.tick(ticks);

  // Shift positions so the bounding box starts at (pad, pad). Both cards and
  // hubs are rendered, so both count toward sizing.
  const pad = NODE_H;
  const xs = simNodes.map((n) => n.x ?? 0);
  const ys = simNodes.map((n) => n.y ?? 0);
  const minX = xs.length ? Math.min(...xs) : 0;
  const minY = ys.length ? Math.min(...ys) : 0;
  const maxX = xs.length ? Math.max(...xs) : 0;
  const maxY = ys.length ? Math.max(...ys) : 0;
  const offX = pad - minX;
  const offY = pad - minY;

  const pos = new Map<string, { x: number; y: number }>();
  const nodes: Laid[] = cardNodes.map((n, i) => {
    const x = (n.x ?? 0) + offX;
    const y = (n.y ?? 0) + offY;
    pos.set(n.id, { x, y });
    return { id: n.id, content: cards[i]!.content, x, y, group: n.group };
  });
  const hubs: Hub[] = hubNodes.map((n) => {
    const x = (n.x ?? 0) + offX;
    const y = (n.y ?? 0) + offY;
    pos.set(n.id, { x, y });
    return { id: n.id, label: n.group!, x, y };
  });

  // Real card-to-card links draw neutral; hub links carry their group for color.
  const edges: Edge[] = simLinks.map((l) => {
    const s = pos.get((l.source as SimNode).id ?? (l.source as unknown as string))!;
    const t = pos.get((l.target as SimNode).id ?? (l.target as unknown as string))!;
    const group = l.hub ? (l.target as SimNode).group ?? null : null;
    return { sx: s.x, sy: s.y, tx: t.x, ty: t.y, group };
  });

  const width = maxX - minX + pad * 2;
  const height = maxY - minY + pad * 2;
  return { nodes, hubs, edges, width: Math.max(width, NODE_W), height: Math.max(height, NODE_H) };
}

// Deterministic color per group value (hue from a stable hash), matching the
// tag-badge palette so a given group reads the same everywhere.
function groupColor(group: string): { border: string; bg: string; text: string } {
  let hash = 0;
  for (let i = 0; i < group.length; i++) {
    hash = (hash * 31 + group.charCodeAt(i)) | 0;
  }
  const hue = ((hash % 360) + 360) % 360;
  return {
    border: `hsl(${hue}, 50%, 65%)`,
    bg: `hsl(${hue}, 65%, 92%)`,
    text: `hsl(${hue}, 45%, 30%)`,
  };
}

// Walk a card document, collecting the uuids of all card_link inline nodes.
function collectLinks(doc: Document): string[] {
  const out: string[] = [];
  const visitInline = (inline: Inline[]): void => {
    for (const node of inline) {
      if (node.kind === "card_link") out.push(node.uuid);
    }
  };
  const visitBlock = (block: Block): void => {
    switch (block.kind) {
      case "paragraph":
      case "heading":
        visitInline(block.content);
        break;
      case "list":
        block.items.forEach((it) => it.content.forEach(visitBlock));
        break;
      case "code_block":
      case "view_block":
        break;
    }
  };
  doc.blocks.forEach(visitBlock);
  return out;
}
